/**
 * Strip internal diagnostic context that the LLM sometimes leaks into
 * customer-facing output (e.g. "[internal - not shown to customer] …").
 * Removes the leading "---" separator as well.
 */
export const stripInternalContext = (text: string): string => {
  const idx = text.search(/\[internal\b/i)
  const withoutInternalMarker =
    idx === -1
      ? text
      : text
          .slice(0, idx)
          .replace(/\s*-{3,}\s*$/, "")
          .trimEnd()

  return stripEmbeddedDiagnosticJson(withoutInternalMarker)
}

const stripEmbeddedDiagnosticJson = (text: string): string => {
  const embeddedJson = findEmbeddedDiagnosticJson(text)
  if (!embeddedJson) {
    return text
  }

  const before = text.slice(0, embeddedJson.start).trimEnd()
  const after = text.slice(embeddedJson.end + 1).trimStart()
  if (before && after) {
    return `${before}\n\n${after}`
  }

  return before || after
}

const findEmbeddedDiagnosticJson = (
  text: string,
): {start: number; end: number} | null => {
  if (
    !text.includes('"probableCauses"') ||
    !text.includes('"suggestedResponses"')
  ) {
    return null
  }

  for (let i = 0; i < text.length; i++) {
    if (text[i] !== "{") continue

    const end = findJsonObjectEnd(text, i)
    if (end === -1) continue

    const jsonText = text.slice(i, end + 1)
    try {
      const parsed = JSON.parse(jsonText)
      const isDiagnosticPayload =
        !!parsed &&
        typeof parsed === "object" &&
        "probableCauses" in parsed &&
        "suggestedResponses" in parsed

      if (isDiagnosticPayload) {
        return {start: i, end}
      }
    } catch {
      // Not valid JSON; continue scanning.
    }

    i = end
  }

  return null
}

const findJsonObjectEnd = (text: string, start: number): number => {
  let depth = 0
  let inString = false
  let escaped = false

  for (let i = start; i < text.length; i++) {
    const char = text[i]

    if (inString) {
      if (escaped) {
        escaped = false
        continue
      }
      if (char === "\\") {
        escaped = true
        continue
      }
      if (char === '"') {
        inString = false
      }
      continue
    }

    if (char === '"') {
      inString = true
      continue
    }

    if (char === "{") {
      depth++
      continue
    }

    if (char === "}") {
      depth--
      if (depth === 0) {
        return i
      }
    }
  }

  return -1
}
